/**
 * PATH: packages/ui/src/kanban/components/lane/LaneBody.tsx
 * FILE_ID: UI-KANBAN-LANE-BODY-001
 * PURPOSE: Expanded-lane body — quote rollup, sort chip, description,
 *          triage tabs, card list / empty state / in-lane load skeleton.
 * WHY: Frame leaf under KanbanLane; owns the scroll surface + empty vs
 *      progressive-load skeleton swap so flat boards never look stuck.
 */
import { useMemo, useRef } from 'react'
import { AlertCircle, ArrowDownUp, Check, Coins, X as XIcon } from 'lucide-react'
import { formatCompactMoney } from '../../utils/quoteLaneValue'
import { AddCardComposer } from '../AddCardComposer'
import { markKanbanScrollActive } from '../kanbanHoverIntent'
import { TriageBulkBar } from '../TriageBulkBar'
import { LaneCardItem } from './LaneCardItem'
import { LaneCardsSkeleton } from './LaneCardsSkeleton'
import { InboxLaneFilterStrip } from './inbox-filters'
import { useVirtualizedLaneCards } from './useVirtualizedLaneCards'
import type { Lane } from '../../types'
import type { KanbanLaneProps } from './types'
import type { LaneVM } from './useKanbanLane'

export function LaneBody({ props, vm }: { props: KanbanLaneProps; vm: LaneVM }) {
  const {
    lane,
    cardsLoading,
    onCardCreate,
    emptyStateHint,
    cardDensity,
    onSetDescription,
    onRequestEditDescription,
    activeSort,
    onClearSort,
    virtualizeCards,
  } = props
  const canEditDescription = !!(onRequestEditDescription || onSetDescription)
  const openDescriptionEditor = (anchorEl: HTMLElement) => {
    if (onRequestEditDescription) {
      const r = anchorEl.getBoundingClientRect()
      onRequestEditDescription(lane, { x: r.left, y: r.bottom + 4 })
      return
    }
    if (!onSetDescription) return
    const next = window.prompt(
      `Edit description for "${lane.name}":`,
      lane.description ?? '',
    )
    if (next === null) return
    void onSetDescription(lane, next.trim())
  }
  const { dragOver, dropLineTop, triageTab, setTriageTab, allocationSplit, showUcAiTabs, sorted, isChecklistMode, showDone, setShowDone, setShowAllCards, showAllRef, flattened, hiddenCardCount, renderList, doneCount, overWipLimit, quoteValue, bulkAcceptAi, bulkRejectAi, showBulkBar, inboxLaneFilters, cardsForLane, laneSearch } = vm
  // Progressive load: shimmer only while awaiting first data AND no cards
  // yet. Settled empty (`cardsLoading` false, sorted empty) → real empty.
  // Skeleton only for cold pending lanes. Never flash white placeholders
  // when the lane already painted cards this session (archive/patch refetch
  // used to set cardsLoading while sorted briefly emptied —
  // BUG-2026-08-03-KANBAN-ARCHIVE-BOARD-WHITEWASH). Parent loadingLaneIds
  // must not include refetching-but-hydrated lanes.
  const showCardsSkeleton = !!cardsLoading && sorted.length === 0

  // Arc V7 — windowed virtualization for heavy Split-view lanes (flag +
  // threshold gated inside the hook; inactive lanes render exactly as
  // before). When active it windows the FULL flattened list, so the
  // CARD_RENDER_CAP expander is superseded and hidden.
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const flattenedIds = useMemo(
    () => flattened.map(({ card: c }) => c.id),
    [flattened],
  )
  const virt = useVirtualizedLaneCards({
    requested: virtualizeCards ?? false,
    laneId: lane.id,
    itemCount: flattened.length,
    cardIds: flattenedIds,
    scrollRef,
    displayMode: lane.display_mode,
    cardDensity,
  })
  return (
    <>
      {/* CRM-QUOTE-PIPELINE — quote-stage value rollup. A thin sub-row
          under the header showing gross Σ value + the risk-weighted figure
          (Σ value × win%) so the funnel reads as money, not just counts.
          Only on quote lanes with at least one valued card. */}
      {quoteValue && (
        <div
          data-testid="kanban-lane-quote-value"
          className="flex items-center justify-between gap-2 border-b border-neutral-200/60 dark:border-white/10 px-2.5 py-1"
        >
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-neutral-700 dark:text-neutral-200"
            style={{ color: 'var(--lane-fg, currentColor)' }}
            title={`Gross value in this stage: $${(quoteValue.gross / 100).toLocaleString()} across ${quoteValue.valued} quote${quoteValue.valued === 1 ? '' : 's'}`}
          >
            <Coins className="h-3 w-3 opacity-70" aria-hidden />
            {formatCompactMoney(quoteValue.gross)}
          </span>
          {quoteValue.weighted > 0 && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300"
              title={`Weighted value (Σ value × win%): $${(quoteValue.weighted / 100).toLocaleString()}`}
            >
              ~{formatCompactMoney(quoteValue.weighted)}
            </span>
          )}
        </div>
      )}

      {/* Active-sort chip — rendered immediately under the header
          when the lane has a per-lane client sort applied. Click the
          ✕ to clear the sort and return to the natural ordering. The
          chip is intentionally compact and uses the lane's accent
          colour so operators can scan a row of lanes and instantly
          see which ones are off-natural-order. */}
      {activeSort && (
        <div className="flex items-center gap-1 border-b border-neutral-200/60 dark:border-white/10 px-2.5 py-0.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-950/40 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
            <ArrowDownUp className="h-2.5 w-2.5" />
            <span>
              Sorted: {activeSort === 'due' ? 'due date' : activeSort}
            </span>
            {onClearSort && (
              <button
                type="button"
                onClick={() => onClearSort(lane)}
                aria-label="Clear sort"
                title="Return to natural order"
                className="ml-0.5 rounded-full p-0.5 hover:bg-blue-100 dark:hover:bg-blue-900/60"
              >
                <XIcon className="h-2.5 w-2.5" />
              </button>
            )}
          </span>
        </div>
      )}

      {/* Lane description sub-line (qf!2667). Truncated row under the
          header; click opens the board inline editor when wired
          (onRequestEditDescription), else legacy window.prompt.
          Empty + editable → "Add description" so a cleared subtitle
          can be restored without hunting the … menu. */}
      {lane.description ? (
        <button
          type="button"
          data-testid="kanban-lane-description"
          onClick={(e) => openDescriptionEditor(e.currentTarget)}
          disabled={!canEditDescription}
          title={
            canEditDescription
              ? `${lane.description} — click to edit`
              : lane.description
          }
          className={[
            'block w-full text-left truncate px-2.5 py-1 text-[10.5px] leading-tight text-neutral-600 dark:text-zinc-400 border-b border-neutral-200/60 dark:border-white/10',
            canEditDescription ? 'hover:bg-neutral-100 dark:hover:bg-zinc-900/60' : '',
          ].join(' ')}
        >
          {lane.description}
        </button>
      ) : canEditDescription ? (
        // Hover-only so empty lanes don't grow a permanent chrome row
        // on every column; … menu / right-click still edit when hidden.
        <button
          type="button"
          data-testid="kanban-lane-add-description"
          onClick={(e) => openDescriptionEditor(e.currentTarget)}
          className="block w-full text-left truncate px-2.5 py-1 text-[10.5px] leading-tight text-neutral-400 border-b border-transparent opacity-0 transition-opacity group-hover/lane:border-neutral-200/60 group-hover/lane:opacity-100 hover:bg-neutral-100 hover:text-neutral-600 dark:text-zinc-500 dark:group-hover/lane:border-white/10 dark:hover:bg-zinc-900/60 dark:hover:text-zinc-300"
        >
          Add description
        </button>
      ) : null}

      {overWipLimit && (
        <div className="flex items-center gap-1.5 border-b border-red-200 bg-red-50 px-3 py-1 text-[11px] text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          <AlertCircle className="w-3 h-3" />
          Over WIP limit
        </div>
      )}

      {inboxLaneFilters.enabled && (
        <InboxLaneFilterStrip
          value={inboxLaneFilters.filters}
          onChange={inboxLaneFilters.onChange}
          onClear={inboxLaneFilters.onClear}
          hubUrl={inboxLaneFilters.hubUrl}
          categoryLoading={inboxLaneFilters.categoryLoading}
          categoryError={inboxLaneFilters.categoryError}
        />
      )}

      {/* Module D — UC | AI segmented tabs on TRIAGE / INBOX_PRIVATE
          lanes. Lane filters the visible cards by allocation source
          so the operator can review AI suggestions in isolation
          from their own user-created cards. */}
      {showUcAiTabs && (
        <div
          role="tablist"
          aria-label="Filter cards by allocation source"
          className="flex items-center gap-0.5 border-b border-neutral-200/70 bg-neutral-50/60 px-2 py-1 dark:border-zinc-700/60 dark:bg-zinc-900/30"
        >
          {([
            { id: 'all' as const, label: 'All', count: cardsForLane.length },
            { id: 'uc' as const, label: 'UC', count: allocationSplit.uc.length },
            { id: 'ai' as const, label: 'AI', count: allocationSplit.ai.length },
          ]).map((tab) => {
            const active = triageTab === tab.id
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => setTriageTab(tab.id)}
                className={[
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors',
                  active
                    ? tab.id === 'ai'
                      ? 'bg-amber-500 text-white'
                      : 'bg-foreground/85 text-background dark:bg-white/90 dark:text-black'
                    : 'text-muted-foreground hover:bg-accent',
                ].join(' ')}
              >
                {tab.label}
                <span
                  className={[
                    'rounded-full px-1 text-[9px] leading-none',
                    active ? 'bg-white/25' : 'bg-neutral-200 dark:bg-zinc-700',
                  ].join(' ')}
                >
                  {tab.count}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {showBulkBar && (
        <TriageBulkBar
          count={allocationSplit.ai.length}
          onAcceptAll={bulkAcceptAi}
          onRejectAll={bulkRejectAi}
        />
      )}

      {/* BUG-2026-05-23-KANBAN-LANE-EMPTY-STATE-COLLAPSE — when the
          lane has zero cards, the previous markup rendered the
          empty-state card at the top of a `flex-1 overflow-y-auto`
          parent. The card collapsed to ~70px (its natural content
          height), and the remaining height sat blank — operator on
          a 440px mobile viewport saw "No cards yet" pinned at the
          top with the entire lower half of the screen empty.
          First-principles fix: when sorted.length === 0, swap the
          parent into `flex flex-col` so the dashed hint can claim
          `flex-1` and centre itself in the available height. The
          scroll behaviour stays identical (overflow-y-auto) for
          the populated case via the conditional class join. */}
      <div
        ref={scrollRef}
        data-lane-scroll=""
        // FEAT-2026-07-24-KANBAN-HOVER-CHROME-DWELL — suppress card hover
        // chrome while this lane scrolls so skim/scroll doesn't expand rows.
        onScroll={() => {
          markKanbanScrollActive()
        }}
        className={[
          // `relative` is the positioning context for the FEAT-2026-07-03
          // insertion line (absolutely positioned in content space so it
          // scrolls with the cards). It has no effect on the in-flow card
          // stack or the arc-V7 virtual spacer (which owns its own context).
          'relative flex-1 overflow-y-auto p-1.5',
          sorted.length === 0
            ? 'flex flex-col'
            // Virtual mode positions rows absolutely inside the spacer;
            // the 6px stack gap moves into each row's paddingBottom.
            : virt.active
              ? ''
              : 'space-y-1.5',
        ].join(' ')}
      >
        {/* FEAT-2026-07-03-KANBAN-DROP-LINE — live insertion indicator. A thin
            accent bar (with a leading dot) drawn at the exact gap a single
            dragged card will land in, so the operator can see "between card 1
            and 2" instead of only the whole-lane highlight. Rendered FIRST so
            the `space-y-1.5` sibling margin never applies to it, and
            `pointer-events-none` so it can't intercept the drop. `dropLineTop`
            is null except during an in-flight single-card drag over this lane. */}
        {dropLineTop != null && (
          <div
            aria-hidden="true"
            data-testid="kanban-drop-line"
            className="pointer-events-none absolute inset-x-1.5 z-10"
            style={{ top: dropLineTop }}
          >
            <div className="relative -translate-y-1/2">
              <span className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500 dark:bg-blue-400" />
              <span className="block h-0.5 w-full rounded-full bg-blue-500 dark:bg-blue-400 shadow-[0_0_6px_rgba(59,130,246,0.55)]" />
            </div>
          </div>
        )}
        {showCardsSkeleton ? (
          <LaneCardsSkeleton cardCount={4} />
        ) : sorted.length === 0 ? (
          // Friendly drop-target hint — replaces the old "(empty)"
          // placeholder. Visible whether or not the lane has a
          // composer, so the operator gets a clear signal that
          // dragging a card here is valid.
          //
          // Dark-mode colours are intentionally white-with-alpha
          // (not zinc/neutral) so they stay legible on every themed
          // lane background as well as the unthemed zinc shell.
          // The previous zinc-500/600 values disappeared on saturated
          // canvases when the operator picked a coloured preset.
          //
          // `flex-1 self-stretch` makes the dashed hint fill the
          // entire scroll-area height (BUG-2026-05-23) so the
          // mobile operator sees a centred empty state, not a
          // tiny card floating above whitespace.
          <div
            data-kanban-lane-empty=""
            className={[
              // VISUAL UPLIFT (2026-05-31) — the empty-lane hint is the
              // dominant surface on a fresh board, so it now reads as a calm,
              // rounded drop-zone rather than a cramped dashed box: softer
              // hairline border, generous padding, a faint hover lift, and a
              // clear "Drop here" accent state. flex-1/self-stretch/items-
              // center/justify-center are pinned (BUG-2026-05-23) and kept.
              'group/empty flex flex-1 self-stretch flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed p-6 text-center text-sm transition-colors duration-150',
              dragOver
                ? 'border-blue-400 bg-blue-50/50 text-blue-700 dark:border-blue-400/80 dark:bg-blue-950/30 dark:text-blue-200'
                : 'border-neutral-300/60 text-neutral-500 hover:border-neutral-300 hover:bg-neutral-100/40 dark:border-white/15 dark:text-white/70 dark:hover:border-white/25 dark:hover:bg-white/[0.03]',
            ].join(' ')}
          >
            {/* FIX-2026-06-15-KANBAN-LIGHT-BOARD — themed lanes are now
                always LIGHT, so the previous `dark:text-white/*` empty-state
                copy rendered white-on-light (invisible) whenever the app was
                in dark mode. Consume `--lane-fg` / `--lane-fg-muted` (same
                pattern as the lane title + count above) so the text follows
                the lane SURFACE luminance — dark, readable text on the light
                themed lane — while `currentColor` keeps the neutral/zinc
                fallback on un-themed boards. dragOver keeps its blue accent. */}
            <span
              className="font-medium"
              style={dragOver ? undefined : { color: 'var(--lane-fg, currentColor)' }}
            >
              {dragOver ? 'Drop here' : laneSearch.trim() ? 'No matching cards' : 'No cards yet'}
            </span>
            {!dragOver && (
              <span
                className="text-[11px] leading-snug"
                style={{ color: 'var(--lane-fg-muted, currentColor)' }}
              >
                {laneSearch.trim()
                  ? 'Clear the lane search to see every card.'
                  : (emptyStateHint
                    ?? (onCardCreate
                      ? 'Drag a card here, or use the composer below.'
                      : 'Cards will land here when classification routes them.'))}
              </span>
            )}
          </div>
        ) : null}
        {/* qb!2870 — checklist mode renders a flat row per card; kanban
            mode renders the Trello-style tile. Stack 12 MR-C wraps every
            card in an indent shell so sub-cards subtend their parent.
            Both branches live in LaneCardItem (UI-KANBAN-LANE-CARDITEM-001);
            this file only chooses HOW the rows are laid out:
            - flow (default): plain stack with content-visibility (UAT P4-b)
            - virtual (arc V7, flag + threshold gated, Split view only):
              a sized spacer with only the visible window mounted. Cards
              outside the window are NOT in the DOM, so they are not drag
              or drop targets until scrolled back in — by design. */}
        {showCardsSkeleton ? null : virt.active ? (
          <div
            data-testid="lane-virtual-spacer"
            style={{ height: virt.totalSize, position: 'relative' }}
          >
            {virt.items.map((vi) => {
              const entry = flattened[vi.index]
              if (!entry) return null
              return (
                <LaneCardItem
                  // ARC-10 linked cards — composite instance key. A card
                  // appears at most once PER lane, but the composite makes
                  // key uniqueness independent of that invariant (and keeps
                  // React state per-presence if a future view mixes lanes).
                  key={`${entry.card.id}:${entry.card.placement_id ?? 'home'}`}
                  props={props}
                  vm={vm}
                  card={entry.card}
                  indent={entry.indent}
                  virtual={{
                    start: vi.start,
                    index: vi.index,
                    measureElement: virt.measureElement,
                  }}
                />
              )
            })}
          </div>
        ) : (
          renderList.map(({ card: c, indent }) => (
            <LaneCardItem
              // ARC-10 — composite instance key (see virtualized branch).
              key={`${c.id}:${c.placement_id ?? 'home'}`}
              props={props}
              vm={vm}
              card={c}
              indent={indent}
            />
          ))
        )}
        {/* PERF-2026-06-09-KANBAN-HUGE-LANE-RENDER-CAP — reveal the
            remaining cards on demand. Only renders when the lane
            exceeds the cap, so normal lanes never see it. Superseded
            by the virtual window (which mounts on demand by scroll),
            hence the !virt.active guard. */}
        {!virt.active && hiddenCardCount > 0 && (
          <button
            ref={showAllRef}
            type="button"
            onClick={() => setShowAllCards(true)}
            data-testid="lane-show-all-cards"
            className="mt-1 w-full rounded-md border border-dashed border-neutral-300/70 px-2.5 py-1.5 text-[11px] font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:border-white/15 dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-neutral-200"
            title={`Show all ${flattened.length} cards in this lane`}
          >
            Show all {flattened.length} cards ({hiddenCardCount} more)
          </button>
        )}
      </div>

      {/* BUG-2026-05-22-KANBAN-CHECKLIST-DONE-STILL-VISIBLE — the
          "Done (N)" toggle that reveals / re-hides ticked rows.
          Renders only in checklist mode AND when at least one card
          is done, so kanban-tile lanes and empty checklists stay
          visually quiet. Sits ABOVE the composer so the toggle
          lands at a predictable spot regardless of how many active
          rows there are. */}
      {isChecklistMode && doneCount > 0 && (
        <button
          type="button"
          onClick={() => setShowDone((p) => !p)}
          className="flex w-full items-center justify-between gap-2 border-t border-neutral-200/80 px-2.5 py-1.5 text-[11px] font-medium text-neutral-500 hover:bg-neutral-100 dark:border-white/15 dark:text-neutral-400 dark:hover:bg-neutral-900"
          aria-expanded={showDone}
          aria-controls={`lane-done-${lane.id}`}
          data-testid="lane-done-toggle"
          title={showDone ? 'Hide completed' : 'Show completed'}
        >
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3 w-3 text-emerald-500" />
            <span>
              Done ({doneCount})
            </span>
          </span>
          <span className="text-[10px] uppercase tracking-wide text-neutral-400">
            {showDone ? 'Hide' : 'Show'}
          </span>
        </button>
      )}

      {onCardCreate && (
        <div
          // MOBILE-KANBAN-FIRST-PRINCIPLES-ARC §3.3 + §3.4 (Phase 2)
          // — hidden on phone (`<md`). The per-lane inline composer
          // at the bottom of every lane is unreachable on a phone
          // for any lane with more than ~3 cards (the operator
          // would have to vertically scroll to the bottom of the
          // lane just to add a card). Replaced on mobile by a
          // page-level floating action button rendered by
          // KanbanBoardView, which tracks the currently snapped-to
          // lane via IntersectionObserver and adds the card
          // there. Above md the inline composer renders exactly
          // as before — desktop UX unchanged.
          className="hidden border-t border-neutral-200/80 dark:border-white/15 p-1 md:block"
        >
          <AddCardComposer
            onCreate={(title, summary) => onCardCreate(lane, title, summary)}
            placeholder={
              lane.display_mode === 'checklist'
                ? 'Add a checklist item…'
                : '+ Add a card'
            }
          />
        </div>
      )}
    </>
  )
}
